import { getDatabase, ref, get, set, update, remove, query, orderByKey, limitToFirst, startAt, startAfter } from "firebase/database";
import { app } from "../firebase/config";

const database = getDatabase(app);

/**
 * Enhanced user database utilities for email authentication
 */

/**
 * Save user to database with support for both Google and email authentication
 * @param {Object} user - User object
 * @param {string} authProvider - 'google' or 'email'
 * @param {Object} additionalData - Additional data for email users
 * @returns {Promise<Object>} - Save result
 */
export const saveUserToDatabase = async (
  user,
  authProvider = "google",
  additionalData = {}
) => {
  try {
    console.log("🔄 Saving user to database with auth provider:", authProvider);

    const userRef = ref(database, `users/${user.uid}`);
    const snapshot = await get(userRef);

    if (!snapshot.exists()) {
      // New user - create with basic information
      const userData = {
        userID: user.uid,
        createdAt: Date.now(),
        dailyPostCount: {},
        profile: {
          displayName: user.displayName || null,
          email: user.email || null,
          photoURL: user.photoURL || null,
          authProvider: authProvider,
        },
        ...additionalData,
      };

      await set(userRef, userData);
      
      // OPTIMIZED: Also save to user_profiles
      try {
        // Create a lightweight profile object
        const userProfileData = {
          id: user.uid,
          createdAt: userData.createdAt,
          profile: userData.profile,
          emailAuth: userData.emailAuth || null,
          membership: userData.membership || null,
          membershipExpiry: userData.membershipExpiry || null,
          isTrial: userData.isTrial || null,
          points: userData.points || null,
          stats: { postCount: 0, createdPostCount: 0 }
        };
        await set(ref(database, `user_profiles/${user.uid}`), userProfileData);
      } catch (e) {
        console.error("Error creating user_profile:", e);
      }

      console.log("✅ New user saved to database:", user.uid);
      return { success: true, isNewUser: true };
    } else {
      // Existing user - update profile if needed
      const userData = snapshot.val();
      const profile = userData.profile || {};

      const updatedProfile = {
        ...profile,
        displayName: profile.displayName || user.displayName || null,
        email: profile.email || user.email || null,
        photoURL: profile.photoURL || user.photoURL || null,
        authProvider: profile.authProvider || authProvider,
      };

      await update(ref(database, `users/${user.uid}/profile`), updatedProfile);
      
      // OPTIMIZED: Also update user_profiles
      try {
        await update(ref(database, `user_profiles/${user.uid}/profile`), updatedProfile);
      } catch (e) {
        console.error("Error updating user_profile:", e);
      }

      console.log("✅ Existing user profile updated:", user.uid);
      return { success: true, isNewUser: false };
    }
  } catch (error) {
    console.error("❌ Error saving user to database:", error);
    return { success: false, error };
  }
};

/**
 * Get user by ID with auth provider information
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - User data with auth info
 */
/**
 * Get user by ID with auth provider information
 * OPTIMIZED: Fetches only specific fields to avoid downloading large collections like 'posts'
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - User data with auth info
 */
export const getUserById = async (userId) => {
  try {
    console.log("🔄 Getting user by ID (Optimized):", userId);
    
    // Define refs for specific fields we need
    const profileRef = ref(database, `users/${userId}/profile`);
    const emailAuthRef = ref(database, `users/${userId}/emailAuth`);
    const membershipRef = ref(database, `users/${userId}/membership`);
    const membershipExpiryRef = ref(database, `users/${userId}/membershipExpiry`);
    const createdAtRef = ref(database, `users/${userId}/createdAt`);
    
    // Fetch in parallel
    const [profileSnap, emailAuthSnap, membershipSnap, membershipExpirySnap, createdAtSnap] = await Promise.all([
      get(profileRef),
      get(emailAuthRef),
      get(membershipRef),
      get(membershipExpiryRef),
      get(createdAtRef)
    ]);

    // Check if user exists (at least profile or emailAuth or createdAt should exist)
    if (!profileSnap.exists() && !emailAuthSnap.exists() && !createdAtSnap.exists()) {
      console.log("❌ User not found in database:", userId);
      return { success: false, error: "User not found", user: null };
    }

    const profile = profileSnap.val();
    const emailAuth = emailAuthSnap.val();
    const membership = membershipSnap.val();
    const membershipExpiry = membershipExpirySnap.val();
    const createdAt = createdAtSnap.val();

    console.log("✅ User data retrieved (Partial):", {
      userId,
      hasProfile: !!profile,
      hasEmailAuth: !!emailAuth,
      authProvider: profile?.authProvider,
      isActive: emailAuth?.isActive,
    });

    const result = {
      success: true,
      error: null,
      user: {
        uid: userId,
        profile,
        emailAuth,
        membership,
        membershipExpiry,
        createdAt,
        // Derived fields
        authProvider: profile?.authProvider || (emailAuth ? "email" : "google"),
        isEmailUser: !!emailAuth,
        isGoogleUser: profile?.authProvider === "google",
        isActive: emailAuth ? emailAuth.isActive : true,
      },
    };

    return result;
  } catch (error) {
    console.error("❌ Error getting user by ID:", error);
    return { success: false, error: error.message, user: null };
  }
};

/**
 * Get all users with auth provider information
 * @returns {Promise<Object>} - All users with auth info
 */
export const getAllUsers = async () => {
  try {
    // OPTIMIZED: Fetch from user_profiles instead of users
    const usersRef = ref(database, "user_profiles");
    const snapshot = await get(usersRef);

    if (!snapshot.exists()) {
      return { success: true, users: [] };
    }

    const usersData = snapshot.val();
    const usersList = Object.entries(usersData).map(([id, data]) => {
      // Use pre-calculated stats from user_profiles if available
      const stats = data.stats || {};
      const totalPostedCount = stats.postCount || 0;
      const totalCreatedCount = stats.createdPostCount || 0;
      
      // Get auth provider info
      const authProvider = data.profile?.authProvider || "google";
      const isEmailUser = data.emailAuth ? true : false;
      const isActive = isEmailUser ? data.emailAuth.isActive : true;

      return {
        id,
        ...data,
        email: data.profile?.email || data.emailAuth?.email || "",
        name: data.profile?.displayName || "",
        postCounts: `${totalPostedCount}`, // Simplified for list view
        totalPostCount: totalPostedCount,
        totalCreatedCount: totalCreatedCount,
        totalCreatedNormalPosts: totalCreatedCount, // Approx
        todayCreatedPosts: 0, // Not available in lightweight profile
        lastCreatedAt: null, // Not available
        authProvider: authProvider,
        isEmailUser: isEmailUser,
        isGoogleUser: authProvider === "google",
        isActive: isActive,
        lastLogin: isEmailUser ? data.emailAuth?.lastLogin : null,
        createdBy: isEmailUser ? data.emailAuth?.createdBy : null,
      };
    });

    // Sort by creation time (newest first)
    const sortedUsers = usersList.sort(
      (a, b) => (b.createdAt || 0) - (a.createdAt || 0)
    );

    return { success: true, users: sortedUsers };
  } catch (error) {
    console.error("❌ Error getting all users:", error);
    return { success: false, error: error.message, users: [] };
  }
};

/**
 * Get users with pagination
 * @param {number} limit - Number of users to fetch
 * @param {string} lastKey - Last user ID from previous page (for pagination)
 * @returns {Promise<Object>} - Paginated users and next key
 */
export const getUsersPaginated = async (limit = 20, lastKey = null) => {
  try {
    console.log(`🔄 Getting users paginated (limit: ${limit}, lastKey: ${lastKey})`);
    // OPTIMIZED: Fetch from user_profiles instead of users
    const usersRef = ref(database, "user_profiles");
    
    let usersQuery;
    
    // Fetch limit + 1 to check if there are more pages
    if (lastKey) {
      usersQuery = query(usersRef, orderByKey(), limitToFirst(limit + 1), startAt(lastKey));
    } else {
      usersQuery = query(usersRef, orderByKey(), limitToFirst(limit + 1));
    }

    const snapshot = await get(usersQuery);

    if (!snapshot.exists()) {
      return { success: true, users: [], nextKey: null };
    }

    const orderedUsers = [];
    snapshot.forEach((childSnapshot) => {
      const id = childSnapshot.key;
      const data = childSnapshot.val();
      
      // Use pre-calculated stats from user_profiles if available
      const stats = data.stats || {};
      const totalPostedCount = stats.postCount || 0;
      const totalCreatedCount = stats.createdPostCount || 0;

      // Get auth provider info
      const authProvider = data.profile?.authProvider || "google";
      const isEmailUser = data.emailAuth ? true : false;
      const isActive = isEmailUser ? data.emailAuth.isActive : true;

      orderedUsers.push({
        id,
        ...data,
        email: data.profile?.email || data.emailAuth?.email || "",
        name: data.profile?.displayName || "",
        postCounts: `${totalPostedCount}`,
        totalPostCount: totalPostedCount,
        totalCreatedCount: totalCreatedCount,
        totalCreatedNormalPosts: totalCreatedCount,
        todayCreatedPosts: 0,
        lastCreatedAt: null,
        authProvider: authProvider,
        isEmailUser: isEmailUser,
        isGoogleUser: authProvider === "google",
        isActive: isActive,
        lastLogin: isEmailUser ? data.emailAuth?.lastLogin : null,
        createdBy: isEmailUser ? data.emailAuth?.createdBy : null,
      });
    });

    // Pagination logic
    if (lastKey) {
      // If we used startAt(lastKey), the first item is the duplicate (the lastKey itself). Remove it.
      if (orderedUsers.length > 0 && orderedUsers[0].id === lastKey) {
        orderedUsers.shift();
      }
    }

    let nextKey = null;
    // Check if we have more items than the limit
    if (orderedUsers.length > limit) {
      nextKey = orderedUsers[orderedUsers.length - 1].id;
      orderedUsers.pop(); // Remove the extra item used for checking next page
    }

    return { 
      success: true, 
      users: orderedUsers, 
      nextKey: nextKey 
    };

  } catch (error) {
    console.error("❌ Error getting paginated users:", error);
    return { success: false, error: error.message, users: [], nextKey: null };
  }
};

/**
 * Update user profile information
 * @param {string} userId - User ID
 * @param {Object} profileData - Profile data to update
 * @returns {Promise<Object>} - Update result
 */
export const updateUserProfile = async (userId, profileData) => {
  try {
    const userRef = ref(database, `users/${userId}/profile`);
    await update(userRef, {
      ...profileData,
      updatedAt: Date.now(),
    });

    // OPTIMIZED: Also update user_profiles
    try {
      const userProfileRef = ref(database, `user_profiles/${userId}/profile`);
      await update(userProfileRef, {
        ...profileData,
        updatedAt: Date.now(),
      });
    } catch (e) {
      console.error("Error updating user_profile:", e);
    }

    console.log("✅ User profile updated:", userId);
    return { success: true, error: null };
  } catch (error) {
    console.error("❌ Error updating user profile:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Update user membership information
 * @param {string} userId - User ID
 * @param {number} membership - Membership level
 * @param {number} membershipExpiry - Expiry timestamp
 * @returns {Promise<Object>} - Update result
 */
export const updateUserMembership = async (
  userId,
  membership,
  membershipExpiry
) => {
  try {
    const userRef = ref(database, `users/${userId}`);
    await update(userRef, {
      membership: membership,
      membershipExpiry: membershipExpiry,
      membershipUpdatedAt: Date.now(),
    });

    // OPTIMIZED: Also update user_profiles
    try {
      const userProfileRef = ref(database, `user_profiles/${userId}`);
      await update(userProfileRef, {
        membership: membership,
        membershipExpiry: membershipExpiry,
        membershipUpdatedAt: Date.now(),
      });
    } catch (e) {
      console.error("Error updating user_profile membership:", e);
    }

    console.log("✅ User membership updated:", userId);
    return { success: true, error: null };
  } catch (error) {
    console.error("❌ Error updating user membership:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete user from database
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Delete result
 */
export const deleteUser = async (userId) => {
  try {
    const userRef = ref(database, `users/${userId}`);
    await remove(userRef);

    console.log("✅ User deleted from database:", userId);
    return { success: true, error: null };
  } catch (error) {
    console.error("❌ Error deleting user:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get users by authentication provider
 * @param {string} authProvider - 'google' or 'email'
 * @returns {Promise<Object>} - Filtered users
 */
export const getUsersByAuthProvider = async (authProvider) => {
  try {
    const allUsersResult = await getAllUsers();

    if (!allUsersResult.success) {
      return allUsersResult;
    }

    const filteredUsers = allUsersResult.users.filter(
      (user) => user.authProvider === authProvider
    );

    return { success: true, users: filteredUsers };
  } catch (error) {
    console.error("❌ Error getting users by auth provider:", error);
    return { success: false, error: error.message, users: [] };
  }
};

/**
 * Get user statistics by authentication method
 * @returns {Promise<Object>} - User statistics
 */
export const getUserStatsByAuthMethod = async () => {
  try {
    const allUsersResult = await getAllUsers();

    if (!allUsersResult.success) {
      return { success: false, error: allUsersResult.error, stats: null };
    }

    const users = allUsersResult.users;
    const stats = {
      total: users.length,
      google: users.filter((u) => u.authProvider === "google").length,
      email: users.filter((u) => u.authProvider === "email").length,
      emailActive: users.filter((u) => u.authProvider === "email" && u.isActive)
        .length,
      emailInactive: users.filter(
        (u) => u.authProvider === "email" && !u.isActive
      ).length,
      premium: users.filter((u) => {
        if (!u.membership) return false;
        if (!u.membershipExpiry) return false;
        try {
          const expiryDate = new Date(u.membershipExpiry);
          const now = new Date();
          return expiryDate > now;
        } catch {
          return false;
        }
      }).length,
      free: 0, // Will be calculated
    };

    stats.free = stats.total - stats.premium;

    return { success: true, error: null, stats };
  } catch (error) {
    console.error("❌ Error getting user stats:", error);
    return { success: false, error: error.message, stats: null };
  }
};

/**
 * Search users by email or name
 * @param {string} searchTerm - Search term
 * @returns {Promise<Object>} - Search results
 */
export const searchUsers = async (searchTerm) => {
  try {
    const allUsersResult = await getAllUsers();

    if (!allUsersResult.success) {
      return allUsersResult;
    }

    const searchLower = searchTerm.toLowerCase();
    const filteredUsers = allUsersResult.users.filter((user) => {
      return (
        (user.id && user.id.toLowerCase().includes(searchLower)) ||
        (user.name && user.name.toLowerCase().includes(searchLower)) ||
        (user.email && user.email.toLowerCase().includes(searchLower))
      );
    });

    return { success: true, users: filteredUsers };
  } catch (error) {
    console.error("❌ Error searching users:", error);
    return { success: false, error: error.message, users: [] };
  }
};

/**
 * Audit log for admin actions
 * @param {string} adminId - Admin user ID
 * @param {string} action - Action performed
 * @param {string} targetUserId - Target user ID
 * @param {Object} details - Additional details
 * @returns {Promise<Object>} - Log result
 */
export const logAdminAction = async (
  adminId,
  action,
  targetUserId,
  details = {}
) => {
  try {
    const logId = `${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
    const logRef = ref(database, `adminLogs/${logId}`);

    const logData = {
      adminId: adminId,
      action: action,
      targetUserId: targetUserId,
      timestamp: Date.now(),
      details: details,
    };

    await set(logRef, logData);
    console.log("✅ Admin action logged:", action);
    return { success: true, error: null };
  } catch (error) {
    console.error("❌ Error logging admin action:", error);
    return { success: false, error: error.message };
  }
};
